// Updated getDocumentType function
export const getDocumentType = (layout, accountGroupRaw, lobRaw) => {
  const accountGroup = (accountGroupRaw || "").trim();
  const LOB = (lobRaw || "").trim();

  // ✅ Combined condition: If accountGroup is Z006 and LOB is 01 (AUTO), return NON-AUTO type
  const isZ006Auto = accountGroup === "Z006" && LOB === "01";

  // ✅ Always return NON-AUTO type for Z006 + AUTO LOB
  if (isZ006Auto || accountGroup === "Z006") {
    return layout === "1"
      ? "TROUT"
      : layout === "2"
        ? "TRIN"
        : layout === "3"
          ? "ZBLK"
          : layout === "4"
            ? "ZRET"
            : layout === "5"
              ? "ZDSR"
              : layout === "6"
                ? "ZBDG"
                : "";
  }

  // ✅ If only LOB is 01 (AUTO) or NBD, use _AUTO
  if (LOB === "01" || LOB === "NBD") {
    return layout === "1"
      ? "TROUT_AUTO"
      : layout === "2"
        ? "TRIN_AUTO"
        : layout === "3"
          ? "ZBLK"
          : layout === "4"
            ? "ZRET"
            : layout === "5"
              ? "ZDSR"
              : layout === "6"
                ? "ZBDG"
                : "";
  }

  // ✅ Else fallback to non-AUTO
  return layout === "1"
    ? "TROUT"
    : layout === "2"
      ? "TRIN"
      : layout === "3"
        ? "ZBLK"
        : layout === "4"
          ? "ZRET"
          : layout === "5"
            ? "ZDSR"
            : layout === "6"
              ? "ZBDG"
              : "";
};

export const filterByKeyValue = (
  array = [],
  key,
  value = "",
  condition = "equal"
) => {
  if (condition === "equal") {
    return array.filter((item) => item[key] === value);
  } else if (condition === "notEqual") {
    return array.filter((item) => item[key] !== value);
  } else if (condition === "notEmpty") {
    return array.filter(
      (item) =>
        item[key] !== null && item[key] !== undefined && item[key] !== ""
    );
  } else if (condition === "empty") {
    return array.filter(
      (item) =>
        item[key] === null || item[key] === undefined || item[key] === ""
    );
  }
  return array;
};

/** Read account group from customer_details / customer API item (field name varies by endpoint). */
export const getCustomerAccountGroup = (customer) => {
  if (!customer || typeof customer !== "object") return "";

  const value =
    customer.Account_Group ??
    customer.accountGroup ??
    customer.AccountGroup ??
    customer.ACCOUNT_GROUP ??
    "";

  const normalized = String(value).trim();
  if (normalized === "undefined" || normalized === "null") {
    return "";
  }
  return normalized;
};

/** Ensure both Account_Group and accountGroup are set for UI + localStorage consumers. */
export const normalizeCustomerDetailItem = (item) => {
  if (!item || typeof item !== "object") return item;

  const accountGroup = getCustomerAccountGroup(item);
  return {
    ...item,
    Account_Group: accountGroup,
    accountGroup,
  };
};

export const normalizeCustomerDetailItems = (items) => {
  if (!items) return [];
  const list = Array.isArray(items) ? items : [items];
  return list.filter(Boolean).map(normalizeCustomerDetailItem);
};

export const getCustomerLineOfBusinessCode = (customer) => {
  if (!customer || typeof customer !== "object") return "";

  const lobRaw = String(customer.LOB || customer.LOB_Text || "")
    .toUpperCase()
    .trim();
  if (lobRaw === "AUTO") return "01";
  if (lobRaw === "FMCG") return "02";
  if (lobRaw === "NBD") return "05";

  const numericLob = String(
    customer.lineOfBusiness || customer.LineOfBusiness || ""
  ).trim();
  if (["01", "02", "05"].includes(numericLob)) return numericLob;

  return lobRaw === "FMCG" ? "02" : "01";
};

export const getCustomerContractType = (customer) => {
  if (!customer || typeof customer !== "object") return "";

  const value =
    customer.contractType ??
    customer.ContractType ??
    customer.Contract_Type ??
    customer.CONTRACT_TYPE ??
    "";

  const normalized = String(value).trim();
  if (normalized === "undefined" || normalized === "null") return "";
  return normalized;
};

/** Single source of truth for Manager customer selection in localStorage. */
export const persistManagerCustomerSelection = (customer) => {
  if (!customer?.CustomerCode) return;

  const bpCode = customer.CustomerCode;
  const bpName = customer.CustomerName || "";
  const lineOfBusiness = getCustomerLineOfBusinessCode(customer);
  const accountGroup = getCustomerAccountGroup(customer);
  const contractType = getCustomerContractType(customer);

  localStorage.setItem("selectedCustomer", bpCode);
  localStorage.setItem("bpCode", bpCode);
  localStorage.setItem("bpName", bpName);
  localStorage.setItem("lineOfBusiness", lineOfBusiness);
  localStorage.setItem("accountGroup", accountGroup);

  if (contractType) {
    localStorage.setItem("contractType", contractType);
  }

  if (customer.CustomerAddress) {
    localStorage.setItem("location", customer.CustomerAddress);
  }

  const warehouse = customer.Whare_House ?? customer.whareHouse ?? customer.warehouse;
  const warehouseName =
    customer.Whare_House_Name ?? customer.whareHouseName ?? customer.warehouseName;

  if (warehouse) {
    localStorage.setItem("whareHouse", warehouse);
    localStorage.setItem("warehouse", warehouse);
  }
  if (warehouseName) {
    localStorage.setItem("whareHouseName", warehouseName);
    localStorage.setItem("warehouseName", warehouseName);
  }
};

/** Normalize BP / customer codes for comparison (strip leading zeros). */
export const normalizeBpCode = (code) => {
  const value = String(code || "").trim();
  if (!value) return "";
  const normalized = value.replace(/^0+/, "");
  return normalized.length > 0 ? normalized : "0";
};

export const bpCodesMatch = (a, b) => {
  const left = normalizeBpCode(a);
  const right = normalizeBpCode(b);
  return Boolean(left) && left === right;
};

/** Find a customer row by BP/customer code across common field names. */
export const findCustomerByCode = (customers = [], code) => {
  if (!code || !Array.isArray(customers)) return null;
  return (
    customers.find((customer) =>
      bpCodesMatch(
        customer?.CustomerCode ??
          customer?.CustomerNo ??
          customer?.bpCode ??
          customer?.value ??
          customer?.To_Customer,
        code
      )
    ) || null
  );
};

export const resolveCustomerAccountGroup = (customers = [], code) => {
  const match = findCustomerByCode(customers, code);
  return getCustomerAccountGroup(match);
};
